import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  GraduationCap, ArrowLeft, ArrowRight, CheckCircle2, BookOpen,
  Layers, Award, Wrench, AlertTriangle, Flame, Plus, Pencil, Trash2, X,
} from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import AdSlot from "../components/AdSlot";
import { toast } from "sonner";

const LEVEL_META = {
  Beginner: { icon: BookOpen, tag: "Level 1" },
  Professional: { icon: Layers, tag: "Level 2" },
  Expert: { icon: Wrench, tag: "Level 3" },
  Master: { icon: Award, tag: "Level 4" },
  "Reference Guide": { icon: AlertTriangle, tag: "Guide" },
};

const EMPTY_COURSE = { title: "", level: "Beginner", kind: "course", order: 99, description: "", modules: [], takeaways: [] };

function CourseEditor({ open, onOpenChange, initial, onSaved }) {
  const isEdit = Boolean(initial?.slug);
  const [form, setForm] = useState(initial || EMPTY_COURSE);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setModule = (i, key, val) => setForm((f) => {
    const modules = [...f.modules];
    modules[i] = { ...modules[i], [key]: val };
    return { ...f, modules };
  });
  const addModule = () => setForm((f) => ({ ...f, modules: [...f.modules, { title: "", summary: "" }] }));
  const removeModule = (i) => setForm((f) => ({ ...f, modules: f.modules.filter((_, idx) => idx !== i) }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        order: Number(form.order) || 99,
        takeaways: Array.isArray(form.takeaways) ? form.takeaways : String(form.takeaways).split("\n").map((t) => t.trim()).filter(Boolean),
        modules: form.modules.filter((m) => m.title.trim()),
      };
      if (isEdit) return (await api.put(`/courses/${initial.slug}`, payload)).data;
      return (await api.post("/courses", payload)).data;
    },
    onSuccess: (data) => { toast.success(isEdit ? "Course updated" : "Course created"); onSaved(data); onOpenChange(false); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const takeawaysText = Array.isArray(form.takeaways) ? form.takeaways.join("\n") : form.takeaways;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-foreground max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="course-editor">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{isEdit ? "Edit Course" : "New Course"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-tech">Title</Label>
              <Input value={form.title} onChange={upd("title")} className="rounded-none mt-1" data-testid="course-title-input" />
            </div>
            <div>
              <Label className="label-tech">Level</Label>
              <Input value={form.level} onChange={upd("level")} className="rounded-none mt-1" data-testid="course-level-input" />
            </div>
            <div>
              <Label className="label-tech">Order</Label>
              <Input type="number" value={form.order} onChange={upd("order")} className="rounded-none mt-1" data-testid="course-order-input" />
            </div>
            <div>
              <Label className="label-tech">Type</Label>
              <select value={form.kind} onChange={upd("kind")} className="w-full h-10 border border-input px-3 mt-1 rounded-none bg-white" data-testid="course-kind-select">
                <option value="course">Course</option>
                <option value="guide">Guide</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="label-tech">Description</Label>
            <Textarea value={form.description} onChange={upd("description")} rows={3} className="rounded-none mt-1" data-testid="course-description-input" />
          </div>
          <div>
            <Label className="label-tech">Key Takeaways (one per line)</Label>
            <Textarea value={takeawaysText} onChange={(e) => setForm((f) => ({ ...f, takeaways: e.target.value.split("\n") }))} rows={4} className="rounded-none mt-1" data-testid="course-takeaways-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-tech">Modules ({form.modules.length})</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-none border-foreground" onClick={addModule} data-testid="add-module-btn"><Plus className="w-3 h-3 mr-1" /> Add module</Button>
            </div>
            <div className="space-y-3 max-h-[38vh] overflow-y-auto border border-border p-3 bg-secondary">
              {form.modules.map((m, i) => (
                <div key={i} className="bg-white border border-border p-3" data-testid={`module-editor-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="font-mono text-xs text-primary mt-2.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1 space-y-2">
                      <Input value={m.title} onChange={(e) => setModule(i, "title", e.target.value)} placeholder="Module title" className="rounded-none" data-testid={`module-title-${i}`} />
                      <Input value={m.summary} onChange={(e) => setModule(i, "summary", e.target.value)} placeholder="One-line summary" className="rounded-none text-sm" data-testid={`module-summary-${i}`} />
                      <Textarea value={m.content || ""} onChange={(e) => setModule(i, "content", e.target.value)} placeholder="Full lesson content" rows={5} className="rounded-none text-sm" data-testid={`module-content-input-${i}`} />
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="rounded-none text-primary shrink-0" onClick={() => removeModule(i)} data-testid={`remove-module-${i}`}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              {form.modules.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No modules yet — click "Add module".</p>}
            </div>
          </div>

          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.title.trim()} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="course-save-btn">
            {save.isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Course"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CourseDetail({ slug, onBack }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const isAdmin = user && user.role === "admin";

  const { data: course, isLoading } = useQuery({
    queryKey: ["course", slug],
    queryFn: async () => (await api.get(`/courses/${slug}`)).data,
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/courses/${slug}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["courses"] }); toast.success("Course deleted"); onBack(); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  if (isLoading || !course) {
    return <div className="py-20 text-center text-muted-foreground">Loading course…</div>;
  }
  const Meta = LEVEL_META[course.level] || { icon: BookOpen, tag: "Course" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-primary mb-6" data-testid="course-back">
        <ArrowLeft className="w-4 h-4" /> All courses
      </button>

      <div className="border border-border bg-white p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 bg-primary flex items-center justify-center">
                <Meta.icon className="w-5 h-5 text-white" />
              </div>
              <span className="label-tech text-primary">{Meta.tag}</span>
            </div>
            <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black" data-testid="course-title">{course.title}</h1>
          </div>
          {isAdmin && (
            <div className="flex gap-2 shrink-0">
              <Button size="icon" variant="outline" className="rounded-none border-foreground" onClick={() => setEditOpen(true)} data-testid="edit-course-btn"><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="outline" className="rounded-none text-primary" onClick={() => { if (window.confirm("Delete this course?")) del.mutate(); }} data-testid="delete-course-btn"><Trash2 className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
        <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">{course.description}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <span className="label-tech border border-foreground px-3 py-1">{course.modules.length} {course.kind === "guide" ? "topics" : "modules"}</span>
          <span className="label-tech border border-border px-3 py-1 text-muted-foreground">{course.level}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl font-bold mb-4">{course.kind === "guide" ? "Mistakes covered" : "Curriculum"}</h2>
          <Accordion type="single" collapsible className="border border-border bg-white">
            {course.modules.map((m, i) => (
              <AccordionItem key={i} value={`m-${i}`} className="border-b border-border last:border-0" data-testid={`module-${i}`}>
                <AccordionTrigger className="px-5 py-4 hover:no-underline text-left">
                  <span className="flex items-start gap-3">
                    <span className="font-mono text-xs text-primary mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-semibold">{m.title}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5 pl-12 text-muted-foreground leading-relaxed" data-testid={`module-content-${i}`}>
                  {(m.content || m.summary || "").split(/\n+/).map((para, pi) => (
                    <p key={pi} className="mb-3 last:mb-0">{para}</p>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="lg:col-span-1 space-y-8">
          <div className="border border-border bg-white p-6">
            <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-4">
              <GraduationCap className="w-5 h-5 text-primary" /> Key takeaways
            </h3>
            <ul className="space-y-3">
              {course.takeaways.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#2A9D8F] mt-0.5 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <AdSlot placement="sidebar" />
        </div>
      </div>

      {isAdmin && editOpen && (
        <CourseEditor
          open={editOpen}
          onOpenChange={setEditOpen}
          initial={course}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["course", slug] }); qc.invalidateQueries({ queryKey: ["courses"] }); }}
        />
      )}
    </div>
  );
}

export default function TrainingPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const isAdmin = user && user.role === "admin";

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });

  if (selected) return <CourseDetail slug={selected} onBack={() => setSelected(null)} />;

  return (
    <div>
      <section className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          <div className="inline-flex items-center gap-2 border border-foreground px-3 py-1 mb-6">
            <Flame className="w-4 h-4 text-primary" />
            <span className="label-tech">Design &amp; Installation</span>
          </div>
          <h1 className="font-display text-5xl lg:text-6xl tracking-tighter font-black max-w-3xl">
            Training that takes you from <span className="text-primary">apprentice to master</span>.
          </h1>
          <p className="mt-5 text-lg text-muted-foreground max-w-2xl">
            Four progressive courses on fire system design and installation, plus a field guide to the
            mistakes that trip up even seasoned crews.
          </p>
          {isAdmin && (
            <Button onClick={() => setNewOpen(true)} className="rounded-none bg-primary hover:bg-[#D62828] font-bold mt-6" data-testid="new-course-btn">
              <Plus className="w-4 h-4 mr-1" /> New Course
            </Button>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {isLoading ? (
          <div className="border border-border bg-white p-16 text-center label-tech text-muted-foreground" data-testid="courses-loading">
            Loading courses…
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-0 border border-border">
          {courses.map((c, i) => {
            const Meta = LEVEL_META[c.level] || { icon: BookOpen, tag: "Course" };
            const isGuide = c.kind === "guide";
            return (
              <motion.button
                key={c.slug}
                onClick={() => setSelected(c.slug)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`group text-left p-8 border-border md:[&:nth-child(odd)]:border-r border-b transition-colors hover:bg-[#0A0A0A] ${isGuide ? "md:col-span-2" : ""}`}
                data-testid={`course-card-${c.slug}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary flex items-center justify-center">
                      <Meta.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="label-tech text-primary">{Meta.tag}</span>
                  </div>
                  <span className="label-tech text-muted-foreground group-hover:text-gray-400">{c.module_count} {isGuide ? "topics" : "modules"}</span>
                </div>
                <h3 className="font-display text-2xl font-bold group-hover:text-white transition-colors">{c.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground group-hover:text-gray-400 transition-colors leading-relaxed">{c.description}</p>
                <span className="inline-flex items-center gap-1 mt-5 text-sm font-bold text-primary">
                  {isGuide ? "Read the guide" : "View curriculum"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
            );
          })}
        </div>
        )}
      </section>

      {isAdmin && newOpen && (
        <CourseEditor
          open={newOpen}
          onOpenChange={setNewOpen}
          initial={null}
          onSaved={() => qc.invalidateQueries({ queryKey: ["courses"] })}
        />
      )}
    </div>
  );
}
